import { ReactNode, forwardRef } from 'react'
import {
  FieldValues,
  useController,
  FieldPath,
  UseControllerProps,
} from 'react-hook-form'
import {
  FormControl,
  FormControlProps,
  FormLabel,
  RadioGroup,
  RadioGroupProps,
} from '@mui/material'

type FormRadioGroupProps<FormValues extends FieldValues> = {
  name: FieldPath<FormValues>
  label?: ReactNode
  radioGroupProps?: RadioGroupProps
  required?: boolean
  useControllerProps?: Omit<UseControllerProps<FormValues>, 'name'>
} & FormControlProps

const FormRadioGroup = forwardRef(function FormTextField<
  FormValues extends FieldValues,
>(
  {
    children,
    name,
    label,
    radioGroupProps,
    required,
    useControllerProps,
    ...props
  }: FormRadioGroupProps<FormValues>,
  ref: React.Ref<HTMLButtonElement>,
) {
  const { field } = useController<FormValues>({
    name,
    ...useControllerProps,
    rules: { required, ...useControllerProps?.rules },
  })
  const { onChange: onRadioGroupChange, ...restRadioGroupProps } =
    radioGroupProps ?? {}
  return (
    <FormControl {...props}>
      {label && <FormLabel>{label}</FormLabel>}
      <RadioGroup
        {...field}
        ref={ref}
        {...restRadioGroupProps}
        // A caller's onChange runs after the form update, not instead of it.
        onChange={(event, value) => {
          field.onChange(event)
          onRadioGroupChange?.(event, value)
        }}
      >
        {children}
      </RadioGroup>
    </FormControl>
  )
})

export default FormRadioGroup
